import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import { useInlineEditContext } from '../../../context/InlineEditContext';
import InlineEditContextMenu, {
  InlineEditContextMenuButton,
} from '../InlineEditContextMenu/InlineEditContextMenu';

const BaseStyles = css`
  cursor: pointer;
`;

const Outline = (outlineOffset = 0) => css`
  outline: 2px solid #0073e6;
  outline-offset: ${outlineOffset}px;
  position: relative;
  z-index: 1;

  box-shadow:
    0 3px 12px 0 rgba(0, 0, 0, 0.14),
    0 5px 12px 0 rgba(0, 0, 0, 0.16);
  background: rgba(255, 255, 255, 0.3);

  & > * {
    z-index: -1;
  }
`;

const OnHoverOutline = (outlineOffset = 0) => css`
  ${Outline(outlineOffset)}
  outline-color: #0073e680; /* 50% opacity */
`;

interface InlineEditOutlineProps {
  isInEditMode?: boolean;
  isActive?: boolean;
  outlineOffset?: number;
}

const InlineEditOutline = styled('div')<InlineEditOutlineProps>`
  ${(props) => props.isInEditMode && BaseStyles};

  ${(props) =>
    props.isInEditMode && props.isActive
      ? Outline(props.outlineOffset)
      : undefined};

  // Used to override the global :focus style coming from Registry's main.css
  // This is why you have to be so careful when writing global styles
  &:focus {
    ${(props) =>
      props.isInEditMode && props.isActive
        ? Outline(props.outlineOffset)
        : undefined};
  }

  &:hover {
    ${(props) =>
      props.isInEditMode && !props.isActive
        ? OnHoverOutline(props.outlineOffset)
        : undefined};
  }
`;

const InlineEditWrapper = styled('div')`
  position: relative;
  width: 100%;
`;

interface InlineEditHandlerProps {
  children: React.ReactNode;
  contextMenuButtons: InlineEditContextMenuButton[];
  outlineOffset?: number;
  className?: string;
  contextMenuPosition?: 'below' | 'inside';
  editModeOverride?: 'default' | 'forceRender' | 'forceHide';
}

const InlineEditHandler = ({
  children,
  contextMenuButtons,
  outlineOffset = 0,
  className,
  contextMenuPosition = 'below',
  editModeOverride = 'default',
}: InlineEditHandlerProps) => {
  const {
    isInEditMode,
    activeInlineEditHandlerRef,
    setActiveInlineEditHandlerRef,
  } = useInlineEditContext();

  const isEditModeActive =
    editModeOverride === 'default'
      ? isInEditMode
      : editModeOverride === 'forceRender';

  const instanceRef = useRef<HTMLDivElement>(null);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (
      instanceRef.current &&
      activeInlineEditHandlerRef?.current &&
      instanceRef.current === activeInlineEditHandlerRef?.current
    ) {
      setIsActive(true);
    } else if (isActive) {
      setIsActive(false);
    }
  }, [instanceRef, activeInlineEditHandlerRef, isActive]);

  const onClicked = () => {
    setActiveInlineEditHandlerRef(instanceRef);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      onClicked();
      event.preventDefault();
    }
  };

  if (!isEditModeActive) {
    return <>{children}</>;
  }

  return (
    <InlineEditWrapper
      ref={instanceRef}
      className={`InlineEditHandler ${className}`}
    >
      <InlineEditOutline
        onClick={onClicked}
        isInEditMode={isEditModeActive}
        isActive={isActive}
        data-testid="InlineEditHandler__Outline"
        outlineOffset={outlineOffset}
        tabIndex={0}
        onKeyDown={onKeyDown}
        className={className}
      >
        {children}
      </InlineEditOutline>

      {isActive && contextMenuButtons.length > 0 && (
        <InlineEditContextMenu
          buttons={contextMenuButtons}
          outlineOffset={outlineOffset}
          position={contextMenuPosition}
          parentRef={instanceRef}
        />
      )}
    </InlineEditWrapper>
  );
};

export default InlineEditHandler;
